import { useMemo, useState } from 'react';
import type { VersionSummaryDto, VersionState } from '../lib/api';
import { stampDate, toIso } from '../lib/format';
import { useKeyMap, type Binding } from '../lib/keys';
import { useT, type MessageKey } from '../lib/i18n';

/**
 * The version bar.
 *
 * A rate card is edited over months, for a season a year out, while the current
 * season is live and being sold. Without versions every edit is a production
 * edit — so this strip is not a convenience, it is the thing that makes the rest
 * of the dashboard safe to touch.
 *
 * ---------------------------------------------------------------------------
 * "LIVE" IS NOT ONE VERSION
 * ---------------------------------------------------------------------------
 *
 * Several versions of one accommodation are live at once, each for its own
 * window of ARRIVALS — that is not an edge case, it is August: next season's
 * card goes on sale while this season is still being sold, and a stay spanning
 * New Year has to be priced by something. The database allows any number of
 * published rows as long as their windows do not overlap.
 *
 * So a chip saying `PUBLISHED` and nothing else is not enough to answer the only
 * question an operator actually has — "which card prices 15 July 2027?" Each
 * published chip states its window, and the lookup at the bottom asks the server
 * to resolve a date exactly as a guest's quote would.
 */

export interface VersionsProps {
  versions: VersionSummaryDto[];
  /** Needed even with no versions at all: the first one has to be named. */
  accommodationId: string;
  accommodationLabel: string;
  /** The season on the header, which a first version defaults to. */
  year: number;
  activeId: string | null;
  onSelect: (id: string) => void;
  onClone: (id: string, opts: { id: string; label: string; seasonYear: number; shiftDays: number; dropLadder: boolean }) => void;
  /** With a window, only arrivals inside it. Without, the whole season year. */
  onPublish: (id: string, window?: { from: string; until: string }) => void;
  onArchive: (id: string) => void;
  /** Change a version's own name. Allowed even once it is live: see `renaming`. */
  onRename: (id: string, label: string) => void;
  onDelete: (id: string) => void;
  /** Create a version where there is none yet, so pricing can start. */
  onCreate: (v: { id: string; label: string; seasonYear: number }) => void;
  /** Ask the server which version prices an arrival. Null when none does. */
  onResolve: (arrival: string) => Promise<string | null>;
  busy?: boolean;
}

const STATE_ORDER: Record<string, number> = { published: 0, draft: 1, archived: 2 };

/**
 * A version's state, in words.
 *
 * The state itself is a closed set the database enforces, so it is safe to
 * translate — unlike a label, which is whatever the operator typed.
 */
const STATE_LABEL: Record<VersionState, MessageKey> = {
  draft: 'versions.stateDraft',
  published: 'versions.statePublished',
  archived: 'versions.stateArchived',
};

export function Versions({
  versions, accommodationId, accommodationLabel, year, activeId,
  onSelect, onClone, onPublish, onArchive, onRename, onDelete, onCreate, onResolve, busy,
}: VersionsProps) {
  const t = useT();
  /**
   * Which of the two copies is being made, or none.
   *
   * REPORTED, by the operator, mid-season: "Ik kom wel de nodige fouten tegen.
   * Hoe kan ik tarieven nu aanpassen?" — answered with "duplicate the version",
   * and then: "ja maar dan gaat ie naar 2028."
   *
   * They were right. A published card is immutable, so correcting one means
   * copying it; and the only copy on offer defaulted its id, its label, its
   * season AND a 364-day date shift to NEXT YEAR. Every field said "you are
   * starting 2028". The season box could be retyped, but nothing suggested
   * that, and the date shift would have moved the whole calendar anyway.
   *
   * Rolling a card forward and correcting the one that is live are different
   * jobs with different answers to every field, so they are two buttons rather
   * than one button and four things to remember to change.
   */
  const [copying, setCopying] = useState<'nextSeason' | 'revise' | null>(null);
  /*
    Renaming, which is not editing.

    REPORTED: "the label of existing versions can't be edited" — true of every
    card the moment it went live, so one named in a hurry stayed named that for
    the season. A label is on no quote and in no price; the API refuses a
    programme on a published version and allows its name.
  */
  const [renaming, setRenaming] = useState(false);
  const [creating, setCreating] = useState(false);
  const [dating, setDating] = useState(false);
  const [resolved, setResolved] = useState<{ arrival: string; versionId: string } | null>(null);
  const active = versions.find((v) => v.id === activeId) ?? null;
  const anyLive = versions.some((v) => v.state === 'published');

  /**
   * The season on the header first, then the rest, newest first.
   *
   * Every version is listed on purpose — looking at last year's card is how you
   * check what you charged, and cloning it forward is how next year starts. But
   * a park with five seasons of history puts the one being worked on somewhere
   * in the middle of a row of chips, and the operator hunts for it every time.
   *
   * Sorting the current season to the front costs nothing and answers "where is
   * the one I am editing" before it is asked. Within a season the live card
   * leads, because that is the one being sold.
   */
  const ordered = useMemo(
    () => [...versions].sort((a, b) => (
      Number(b.seasonYear === year) - Number(a.seasonYear === year)
      || b.seasonYear - a.seasonYear
      || (STATE_ORDER[a.state] ?? 9) - (STATE_ORDER[b.state] ?? 9)
      || a.label.localeCompare(b.label)
    )),
    [versions, year],
  );

  useKeyMap(useMemo<Binding[]>(() => [
    {
      combo: 'mod+shift+n',
      label: 'versions.cloneIntoNextSeason',
      group: 'keys.groupVersions',
      run: () => setCopying('nextSeason'),
      when: () => active !== null,
    },
  ], [active]));

  return (
    <>
      <div className="vbar">
        {ordered.map((v) => {
          const window = windowOf(v);
          return (
            <button
              type="button"
              key={v.id}
              className={`vchip ${v.state}${v.id === activeId ? ' on' : ''}`}
              onClick={() => onSelect(v.id)}
              title={window
                ? t('versions.windowTitle', { from: window.from, until: window.until })
                : t('versions.chipTitle', {
                  // As above: the park's day, not a truncated UTC instant.
                  id: v.id, count: v.rates, updated: stampDate(v.updatedAt),
                })}
            >
              <span className="vy">{v.seasonYear}</span>
              <span className="vl">{v.label}</span>
              <span className="vs">{t(STATE_LABEL[v.state])}</span>
              {/* When it was made, on the chip rather than only in the tooltip
                  and the panel below. With ten cards on an accommodation the
                  labels stop being enough to tell them apart, and the date is
                  what somebody actually reaches for — "the one I built on
                  Monday". Hovering each in turn to find it is not scanning. */}
              <span className="vd">{stampDate(v.createdAt)}</span>
              {/* Only a live version has a window worth stating: a draft prices
                  nothing until it is published, and inventing a window for it
                  would read as a promise. Absent rather than "not stated",
                  because the dashboard cannot tell a window nobody stated from
                  one the versions endpoint did not send — see the note under
                  the lookup below. */}
              {v.state === 'published' && window && (
                <span className="vw">
                  {t('versions.window', { from: window.from, until: window.until })}
                </span>
              )}
              {resolved?.versionId === v.id && (
                <span className="vr">{t('versions.whichServes')}</span>
              )}
            </button>
          );
        })}
        {ordered.length === 0 && (
          <div className="stack" style={{ gap: 10 }}>
            <p className="note">{t('versions.noneYet')}</p>
            <div className="row">
              <button
                type="button" className="btn pri" disabled={busy}
                onClick={() => setCreating(true)}
              >
                {t('versions.createFirst')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/*
        WHEN, which nothing said.

        REPORTED: "the date a version is created or last modified is not
        mentioned." Both have been on every row the API sends since versions
        existed; the screen simply never drew them. On a card with three
        versions in one season — an original, a correction, a draft — "which of
        these did I do this morning" is otherwise unanswerable.

        Created AND changed, because they answer different questions: when this
        season's work started, and whether anybody has touched it since.
      */}
      {active && (
        <p className="note" style={{ marginTop: 10 }}>
          {t('versions.created', { when: stampDate(active.createdAt) })}
          {active.updatedAt !== active.createdAt
            && ` · ${t('versions.changed', { when: stampDate(active.updatedAt) })}`}
        </p>
      )}

      {active && (
        <div className="row" style={{ marginTop: 10 }}>
          <button
            type="button" className="btn"
            onClick={() => setCopying('nextSeason')} disabled={busy}
          >
            {t('versions.clone')}
          </button>
          <button type="button" className="btn" onClick={() => setRenaming(true)} disabled={busy}>
            {t('versions.rename')}
          </button>
          {/*
            Offered for ANY version, not only a live one.

            REPORTED: "when cloning it seems to default to next season, which
            you often don't want." It was, and this was the answer — except it
            only appeared for a published card, so copying a draft to try a
            variant, or taking an archived card back, still meant retyping the
            year and turning off a 364-day date shift.
          */}
          <button
            type="button" className="btn"
            onClick={() => setCopying('revise')} disabled={busy}
          >
            {t('versions.revise')}
          </button>
          <button
            type="button"
            className="btn pri"
            onClick={() => onPublish(active.id)}
            disabled={busy || active.state === 'published'}
            title={active.state === 'published'
              ? t('versions.alreadyLive')
              : t('versions.publishTitle')}
          >
            {t('versions.publish')}
          </button>
          {/* A second button rather than a dialog in front of the first: the
              common case is "take it live for the season it was written for",
              and making that two clicks to serve the rarer case would tax every
              publish for the benefit of a few. */}
          <button
            type="button" className="btn" disabled={busy}
            onClick={() => setDating(true)}
          >
            {t('versions.publishForDates')}
          </button>
          <button
            type="button" className="btn" disabled={busy || active.state === 'archived'}
            onClick={() => onArchive(active.id)}
          >
            {t('versions.archive')}
          </button>
          <button
            type="button" className="btn danger"
            disabled={busy || active.state === 'published'}
            onClick={() => onDelete(active.id)}
            title={active.state === 'published'
              ? t('versions.deleteLiveTitle')
              : t('versions.deleteDraftTitle')}
          >
            {t('versions.delete')}
          </button>
          {active.state === 'published' && (
            <span className="note">{t('versions.liveIsReadOnly')}</span>
          )}
        </div>
      )}

      {anyLive && (
        <ArrivalLookup
          versions={versions}
          resolved={resolved}
          busy={busy}
          onAsk={async (arrival) => {
            const id = await onResolve(arrival);
            setResolved(id ? { arrival, versionId: id } : null);
          }}
        />
      )}

      {renaming && active && (
        <RenameForm
          source={active}
          onCancel={() => setRenaming(false)}
          onSubmit={(label) => { onRename(active.id, label); setRenaming(false); }}
        />
      )}

      {copying && active && (
        <CloneForm
          mode={copying}
          taken={versions.map((v) => v.id)}
          source={active}
          onCancel={() => setCopying(null)}
          onSubmit={(opts) => { onClone(active.id, opts); setCopying(null); }}
        />
      )}

      {dating && active && (
        <PublishWindowForm
          source={active}
          onCancel={() => setDating(false)}
          onSubmit={(w) => { onPublish(active.id, w); setDating(false); }}
        />
      )}

      {creating && (
        <FirstVersionForm
          accommodationId={accommodationId}
          accommodationLabel={accommodationLabel}
          year={year}
          taken={versions.map((v) => v.id)}
          onCancel={() => setCreating(false)}
          onSubmit={(v) => { onCreate(v); setCreating(false); }}
        />
      )}
    </>
  );
}

/**
 * A published version's window of arrivals, as dates.
 *
 * The API stores and sends them as day numbers, and omits them entirely for a
 * version whose window was never stated — which the store reads as covering
 * everything, so it cannot be drawn as a range.
 */
function windowOf(v: VersionSummaryDto): { from: string; until: string } | null {
  if (v.validFrom == null || v.validUntil == null) return null;
  return { from: toIso(v.validFrom), until: toIso(v.validUntil) };
}

/**
 * "Which card prices an arrival on this date?"
 *
 * Asked of the server rather than worked out here. The rule — the live table
 * whose window contains the arrival — lives in one SQL statement, and a second
 * copy of it in the browser would be a copy that can disagree with the price a
 * guest is actually quoted, which is the one thing this panel exists to rule
 * out.
 */
function ArrivalLookup({ versions, resolved, onAsk, busy }: {
  versions: VersionSummaryDto[];
  resolved: { arrival: string; versionId: string } | null;
  onAsk: (arrival: string) => void;
  busy?: boolean;
}) {
  const t = useT();
  const [arrival, setArrival] = useState('');
  const answer = resolved ? versions.find((v) => v.id === resolved.versionId) : null;
  /* Every live version arrived without a window. That is not a park that never
     stated one — the versions endpoint does not send the two columns at all — so
     the chips have nothing to show and this is the only way to ask. Said out
     loud rather than left as an empty space, and it disappears by itself the day
     the API sends them. The look-up asks the quote route, which resolves an
     arrival exactly as a guest's quote does, so the answer is the real one and
     not this screen's reconstruction of the rule. */
  const noWindows = versions.every((v) => v.state !== 'published' || windowOf(v) === null);

  return (
    <div style={{ marginTop: 16 }}>
      <h3 className="eyebrow">{t('versions.whichHeading')}</h3>
      <p className="note" style={{ margin: '6px 0 10px' }}>{t('versions.whichNote')}</p>
      {noWindows && (
        <p className="note" style={{ margin: '0 0 10px' }}>{t('versions.windowsUnavailable')}</p>
      )}
      <div className="row">
        <div className="field" style={{ maxWidth: 190 }}>
          <label htmlFor="varr">{t('versions.whichArrival')}</label>
          <input
            id="varr" type="date" value={arrival}
            onChange={(e) => setArrival(e.target.value)}
          />
        </div>
        <button
          type="button" className="btn" disabled={!arrival || busy}
          onClick={() => onAsk(arrival)}
        >
          {t('versions.whichAsk')}
        </button>
        {answer && resolved && (
          <span className="note">
            {t('versions.whichAnswer', { label: answer.label, date: resolved.arrival })}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * The first version of an accommodation.
 *
 * Everything else on this bar needs a version to already exist — Clone needs
 * something to clone, Publish and Archive need something selected — so without
 * this an accommodation created without one could never be priced at all.
 */
function FirstVersionForm({ accommodationId, accommodationLabel, year, taken, onSubmit, onCancel }: {
  accommodationId: string;
  accommodationLabel: string;
  year: number;
  taken: string[];
  onSubmit: (v: { id: string; label: string; seasonYear: number }) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [season, setSeason] = useState(year);
  /** The raw text while it is being typed. See the input below. */
  const [seasonText, setSeasonText] = useState(String(year));
  const [label, setLabel] = useState(String(year));
  const [id, setId] = useState(`${accommodationId}-${year}`);
  const clash = taken.includes(id.trim());

  return (
    <section className="panel" style={{ marginTop: 12 }}>
      <div className="panel-hd">
        <h2>{t('versions.createFirstHeading', { label: accommodationLabel })}</h2>
      </div>
      <div className="panel-bd">
        <div className="controls">
          <div className="field">
            <label htmlFor="fvid">{t('versions.newId')}</label>
            <input id="fvid" value={id} onChange={(e) => setId(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="fvlabel">{t('versions.label')}</label>
            <input id="fvlabel" value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="fvyear">{t('versions.season')}</label>
            {/* Text for the same reason as the clone form's: a number input
                bound to `Number(x) || year` cannot be cleared to retype. */}
            <input
              id="fvyear"
              type="text"
              inputMode="numeric"
              value={seasonText}
              onChange={(e) => {
                setSeasonText(e.target.value);
                const next = Number(e.target.value.trim());
                if (!Number.isInteger(next) || next < 1970 || next > 3000) return;
                setSeason(next);
                setLabel(String(next));
                setId(`${accommodationId}-${next}`);
              }}
              onBlur={() => setSeasonText(String(season))}
            />
          </div>
        </div>
        {clash && (
          <div className="diag" style={{ marginTop: 12 }}>
            <div className="dg error">
              <span className="ic" aria-hidden="true">✕</span>
              <span>{t('versions.idTaken', { id })}</span>
            </div>
          </div>
        )}
        <div className="row" style={{ marginTop: 14 }}>
          <button
            type="button" className="btn pri"
            disabled={!id.trim() || !label.trim() || clash}
            onClick={() => onSubmit({ id: id.trim(), label: label.trim(), seasonYear: season })}
          >
            {t('versions.create')}
          </button>
          <button type="button" className="btn" onClick={onCancel}>{t('versions.cancel')}</button>
        </div>
      </div>
    </section>
  );
}

/**
 * Publish for a stated window of arrivals.
 *
 * Both ends or neither — the API refuses half a window rather than guessing the
 * other end from the season, and guessing is exactly what makes a published
 * window differ from the one somebody asked for.
 */
function PublishWindowForm({ source, onSubmit, onCancel }: {
  source: VersionSummaryDto;
  onSubmit: (w: { from: string; until: string }) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const existing = windowOf(source);
  const [from, setFrom] = useState(existing?.from ?? `${source.seasonYear}-01-01`);
  const [until, setUntil] = useState(existing?.until ?? `${source.seasonYear + 1}-01-01`);
  const backwards = until <= from;

  return (
    <section className="panel" style={{ marginTop: 12 }}>
      <div className="panel-hd">
        <h2>{t('versions.publishForDatesHeading', { label: source.label })}</h2>
      </div>
      <div className="panel-bd">
        <p className="note" style={{ marginBottom: 12 }}>{t('versions.publishForDatesNote')}</p>
        <div className="controls">
          <div className="field">
            <label htmlFor="pwfrom">{t('versions.publishFrom')}</label>
            <input
              id="pwfrom" type="date" value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="pwuntil">{t('versions.publishUntil')}</label>
            <input
              id="pwuntil" type="date" value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </div>
        </div>
        {backwards && (
          <div className="diag" style={{ marginTop: 12 }}>
            <div className="dg error">
              <span className="ic" aria-hidden="true">✕</span>
              <span>{t('versions.windowBackwards')}</span>
            </div>
          </div>
        )}
        <div className="row" style={{ marginTop: 14 }}>
          <button
            type="button" className="btn pri" disabled={backwards}
            onClick={() => onSubmit({ from, until })}
          >
            {t('versions.publishConfirm')}
          </button>
          <button type="button" className="btn" onClick={onCancel}>{t('versions.cancel')}</button>
        </div>
      </div>
    </section>
  );
}

function CloneForm({ source, mode, taken, onSubmit, onCancel }: {
  source: VersionSummaryDto;
  /** Roll the card into next season, or correct the one that is live. */
  mode: 'nextSeason' | 'revise';
  /** Version ids already in use, so a revision does not collide with one. */
  taken: readonly string[];
  onSubmit: (o: { id: string; label: string; seasonYear: number; shiftDays: number; dropLadder: boolean }) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const revising = mode === 'revise';
  const startYear = revising ? source.seasonYear : source.seasonYear + 1;
  /*
    A revision keeps the season and the calendar; only the id has to differ,
    because a version id is a primary key and the card being corrected still
    holds the obvious one. `-r2`, `-r3`, … reads as what it is, and publishing
    the result archives the card it was taken from — `publishVersion` already
    supersedes any published version whose window it overlaps, so there is no
    second live card to clean up.
  */
  const freeId = () => {
    const base = `${source.accommodationId}-${startYear}`;
    if (!revising && !taken.includes(base)) return base;
    for (let n = 2; n < 100; n += 1) if (!taken.includes(`${base}-r${n}`)) return `${base}-r${n}`;
    return `${base}-r`;
  };
  const [id, setId] = useState(freeId);
  const [label, setLabel] = useState(
    revising ? t('versions.reviseLabel', { label: source.label }) : String(startYear),
  );
  const [year, setYear] = useState(startYear);
  /** The raw text while it is being typed. See the input below. */
  const [yearText, setYearText] = useState(String(startYear));
  // No shift when correcting: the dates are already the dates the guest books.
  const [shift, setShift] = useState(revising ? 0 : 364);
  const [dropLadder, setDropLadder] = useState(false);

  return (
    <section className="panel" style={{ marginTop: 12 }}>
      <div className="panel-hd">
        <h2>
          {t(revising ? 'versions.reviseHeading' : 'versions.cloneHeading',
            { label: source.label })}
        </h2>
      </div>
      <div className="panel-bd">
        <div className="controls">
          <div className="field">
            <label htmlFor="cid">{t('versions.newId')}</label>
            <input id="cid" value={id} onChange={(e) => setId(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="clabel">{t('versions.label')}</label>
            <input id="clabel" value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cyear">{t('versions.season')}</label>
            {/* Text, not a number bound to the parsed value.
                `Number(x) || nextYear` cannot be cleared: emptying the box
                parses to 0, falls back, and re-renders the old year — so the
                only way to change it is to overtype four digits exactly. The
                same shape of bug made the header's season control unusable and
                the price grid reject cents. */}
            <input
              id="cyear"
              type="text"
              inputMode="numeric"
              value={yearText}
              onChange={(e) => {
                setYearText(e.target.value);
                const n = Number(e.target.value.trim());
                if (Number.isInteger(n) && n > 1970 && n < 3000) setYear(n);
              }}
              onBlur={() => setYearText(String(year))}
            />
          </div>
          <div className="field">
            <label htmlFor="cshift">{t('versions.shiftBy')}</label>
            <select id="cshift" value={shift} onChange={(e) => setShift(Number(e.target.value))}>
              <option value={364}>{t('versions.shift364')}</option>
              <option value={365}>{t('versions.shift365')}</option>
              <option value={0}>{t('versions.shiftNone')}</option>
            </select>
          </div>
        </div>
        <p className="note" style={{ marginTop: 10 }}>
          {t(revising ? 'versions.reviseNote' : 'versions.shiftNote')}
        </p>
        <label className="check" style={{ marginTop: 10 }}>
          <input
            type="checkbox" checked={dropLadder}
            onChange={(e) => setDropLadder(e.target.checked)}
          />
          {t('versions.dropLadder')}
        </label>
        <div className="row" style={{ marginTop: 14 }}>
          <button
            type="button" className="btn pri"
            onClick={() => onSubmit({ id, label, seasonYear: year, shiftDays: shift, dropLadder })}
            disabled={!id.trim() || !label.trim()}
          >
            {t('versions.createClone')}
          </button>
          <button type="button" className="btn" onClick={onCancel}>{t('versions.cancel')}</button>
        </div>
      </div>
    </section>
  );
}


/**
 * A version's own name.
 *
 * Separate from `CloneForm` because it is the opposite operation: that one makes
 * a second card, this one leaves the card alone and changes what it is called.
 * Confusing the two is how an operator ends up with four cards for one season.
 */
function RenameForm({ source, onSubmit, onCancel }: {
  source: VersionSummaryDto;
  onSubmit: (label: string) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [label, setLabel] = useState(source.label);
  return (
    <section className="panel" style={{ marginTop: 12 }}>
      <div className="panel-hd"><h2>{t('versions.renameHeading', { label: source.label })}</h2></div>
      <div className="panel-bd">
        <div className="controls">
          <div className="field">
            <label htmlFor="rlabel">{t('versions.label')}</label>
            <input id="rlabel" value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>
        </div>
        <p className="note" style={{ marginTop: 10 }}>{t('versions.renameNote')}</p>
        <div className="row" style={{ marginTop: 10 }}>
          <button
            type="button" className="btn pri"
            disabled={label.trim() === '' || label === source.label}
            onClick={() => onSubmit(label.trim())}
          >
            {t('versions.renameConfirm')}
          </button>
          <button type="button" className="btn" onClick={onCancel}>{t('versions.cancel')}</button>
        </div>
      </div>
    </section>
  );
}
